import { Form } from 'semantic-ui-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import styled from 'styled-components';
import { Info, Warning, WarningCircle } from '@phosphor-icons/react';
import { ItemImage, SkupremeModal } from '../../components';
import {
  ColoredPill,
  ControlledTextAreaFormField,
  DropdownFormField,
  HoverBorderButton,
  MuteText,
  NumberFormField,
  RadioFormField,
  StyledButton,
} from '../../elements';
import {
  CommonUtility,
  InventoryAdjustmentTypes,
  InventoryService,
  theme,
  ToastMessage,
} from '../../utility';
import { InventoryProposalSearch } from './inventoryProposalSearch';

const ItemSchema = yup.object().shape({
  inventoryId: yup.string().required('Select a Lot'),
  adjustmentType: yup.string().required('Adjustment type is required'),
  quantity: yup
    .number()
    .when('adjustmentType', {
      is: InventoryAdjustmentTypes.overwrite,
      then: schema => schema.min(0, 'Quantity is required'),
      otherwise: schema => schema.positive('Quantity is required'),
    })
    .typeError('Quantity is required'),
  note: yup.string().required('Note is required'),
  transferMethod: yup.string(),
});

const WarningIcon = styled(Warning)`
  margin-right: 5px;
  margin-bottom: -2px;
`;

const RadioFormFieldContianer = styled.div`
  .radio-container {
    &.warning-container {
      background: #fef5e7;
    }
    padding: 3px 5px;
    border-radius: 15px;
  }
`;

const adjusmentOptions = [
  {
    text: 'Increase',
    value: InventoryAdjustmentTypes.increase,
    key: InventoryAdjustmentTypes.increase,
  },
  {
    text: 'Decrease',
    value: InventoryAdjustmentTypes.decrease,
    key: InventoryAdjustmentTypes.decrease,
  },
  {
    text: 'Overwrite',
    value: InventoryAdjustmentTypes.overwrite,
    key: InventoryAdjustmentTypes.overwrite,
  },
  {
    text: 'Transfer',
    value: InventoryAdjustmentTypes.transfer,
    key: InventoryAdjustmentTypes.transfer,
  },
];

const OptionContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  column-gap: 5px;
  row-gap: 5px;
`;

export function InventoryAdjustmentPopup({ onClose, item, refresh, open, warehouseId }) {
  const [loading, setLoading] = useState(false);
  const [destinationLocation, setDestinationLocation] = useState('');

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(ItemSchema),
    shouldUnregister: true,
    mode: 'onChange',
  });

  const [inventoryId, quantity, adjustmentType, transferMethod] = watch([
    'inventoryId',
    'quantity',
    'adjustmentType',
    'transferMethod',
  ]);

  const onConfirm = async formValues => {
    try {
      if (
        adjustmentType === InventoryAdjustmentTypes.transfer &&
        !destinationLocation._id
      ) {
        ToastMessage.error('Destination is required');
        return;
      }

      setLoading(true);
      const payload = {
        adjustmentType: formValues.adjustmentType,
        quantity:
          formValues.adjustmentType === InventoryAdjustmentTypes.overwrite
            ? {
                available: formValues.quantity,
                uom: 'units',
              }
            : formValues.quantity,
        note: formValues.note,
      };

      if (formValues.adjustmentType === InventoryAdjustmentTypes.transfer) {
        payload.destinationLocationId = destinationLocation._id;
        payload.transferMethod = formValues.transferMethod;
      }

      await InventoryService.updateInventory(formValues.inventoryId, payload);
      refresh();
      ToastMessage.success('Inventory adjusted successfully');
      onClose();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (CommonUtility.isValidObject(item)) {
      const resetValues = {
        note: '',
        quantity: 0,
        inventoryId: '',
        transferMethod: 'items',
      };

      if (!Number.isNaN(item.lotIndex) && item.lotIndex !== -1) {
        resetValues.inventoryId = item.locationSummary[item.lotIndex]?.inventoryId;
      }

      if (item?.transferLot) {
        resetValues.adjustmentType = InventoryAdjustmentTypes.transfer;
      }
      reset(resetValues);
      return;
    }

    reset({
      note: '',
      quantity: 0,
      inventoryId: '',
      adjustmentType: '',
      transferMethod: 'items',
    });
  }, [item]);

  const lotOptions = useMemo(() => {
    if (!CommonUtility.isValidObject(item)) return [];

    return (
      item.locationSummary?.map(location => {
        const zoneColor = location?.zone?.color;
        const zoneCode = location?.zone?.code;
        const locationCode = location?.location?.code;
        const palletNumber = location?.pallet?.palletNumber;

        return {
          text: (
            <span className="d-flex align-items-center">
              {location.lotNumber}
              <OptionContainer className="d-flex flex-wrap ml-2">
                <div className="d-flex align-items-center">
                  <ColoredPill mini color={zoneColor}>
                    {zoneCode}
                  </ColoredPill>
                  <ColoredPill mini className="ml-1" color={zoneColor}>
                    {locationCode}
                  </ColoredPill>
                </div>
                {palletNumber && (
                  <ColoredPill mini color={theme.colors.grey}>
                    {palletNumber}
                  </ColoredPill>
                )}
              </OptionContainer>
            </span>
          ),
          value: location.inventoryId,
        };
      }) || []
    );
  }, [item]);

  const currentQuantity = useMemo(() => {
    if (inventoryId && CommonUtility.isValidObject(item)) {
      const location = item?.locationSummary.find(x => x.inventoryId === inventoryId);
      if (location) {
        return location.quantity?.available;
      }
    }
    return 0;
  }, [inventoryId, item]);

  useEffect(() => {
    if (transferMethod === 'pallet') {
      setValue('quantity', currentQuantity, { shouldValidate: true });
    }
  }, [transferMethod]);

  const newInventory = useMemo(() => {
    if (!adjustmentType) return currentQuantity || 0;

    if (adjustmentType === InventoryAdjustmentTypes.overwrite) {
      return Number(quantity || 0);
    }

    if (!quantity) return currentQuantity || 0;

    if (adjustmentType === InventoryAdjustmentTypes.increase) {
      return Number(currentQuantity || 0) + Number(quantity || 0);
    }

    return Number(currentQuantity || 0) - Number(quantity || 0);
  }, [quantity, adjustmentType, currentQuantity]);

  const transferView = useMemo(
    () => adjustmentType === InventoryAdjustmentTypes.transfer,
    [adjustmentType],
  );

  const hasError = useMemo(() => {
    const isOriginBin = !item?.locationSummary?.[item?.lotIndex]?.pallet?.palletNumber;
    if (isOriginBin && transferMethod === 'pallet' && !destinationLocation?.isEmpty) {
      return true;
    }
    return false;
  }, [destinationLocation, transferMethod]);

  return (
    <SkupremeModal size="tiny" title="Inventory Adjustment" onClose={onClose} open={open}>
      <div className="d-flex align-items-center mb-3">
        <ItemImage
          imageKey="p_images"
          displayImageKey="display_image"
          item={item?.product}
          size="mini"
        />
        <div className="ml-2">
          <div className="d-flex">
            <MuteText>SKU</MuteText>
            <b className="ml-2">{item?.product?.p_sku}</b>
          </div>
        </div>
      </div>
      <Form onSubmit={handleSubmit(onConfirm)}>
        <DropdownFormField
          control={control}
          name="adjustmentType"
          error={errors.adjustmentType}
          hint={errors.adjustmentType?.message}
          label="Type"
          options={adjusmentOptions}
        />
        <DropdownFormField
          control={control}
          name="inventoryId"
          error={errors.inventoryId}
          hint={errors.inventoryId?.message}
          label="Lot"
          options={lotOptions}
        />
        {transferView && (
          <>
            <RadioFormFieldContianer>
              <RadioFormField
                control={control}
                name="transferMethod"
                label="How would you like to transfer the inventory?"
                options={[
                  {
                    text: 'Just Unit Goods',
                    value: 'items',
                    icon: destinationLocation?.isEmpty ? (
                      <WarningIcon color={theme.colors.orange} size={14} />
                    ) : null,
                    className: destinationLocation?.isEmpty ? 'warning-container' : '',
                  },
                  {
                    text: 'Move Entire Pallet',
                    value: 'pallet',
                    icon: destinationLocation?.hasPallet ? (
                      <WarningIcon color={theme.colors.orange} size={14} />
                    ) : null,
                    className: destinationLocation?.hasPallet ? 'warning-container' : '',
                  },
                ]}
                error={errors.transferMethod}
                hint={errors.transferMethod?.message}
              />
            </RadioFormFieldContianer>
            <div className="mb-4">
              <div className="d-flex align-items-center justify-content-between">
                <MuteText>Destination Location</MuteText>
                {destinationLocation && (
                  <MuteText>{destinationLocation?.hasPallet ? 'Pallet' : 'Bin'}</MuteText>
                )}
              </div>
              <InventoryProposalSearch
                warehouseId={warehouseId}
                productId={item?.productId}
                currentQuantity={currentQuantity}
                newInventory={newInventory}
                onResultClick={item =>
                  setDestinationLocation({
                    hasPallet: item.hasPallet,
                    isEmpty: item.isEmpty,
                    ...item.location,
                  })
                }
              />
              {destinationLocation && <MuteText>{destinationLocation?.code}</MuteText>}
              <TransferMessage
                destinationHasPallet={destinationLocation?.hasPallet}
                originTransferPallet={transferMethod === 'pallet'}
                isEmpty={destinationLocation?.isEmpty}
                isOriginBin={
                  !item?.locationSummary?.[item?.lotIndex]?.pallet?.palletNumber
                }
              />
            </div>
          </>
        )}
        <div className="d-flex flex-column">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <MuteText>Quantity</MuteText>
            {inventoryId && (
              <div>
                <MuteText>Current Inventory</MuteText>
                <span className="ml-1">{currentQuantity}</span>
              </div>
            )}
          </div>
          <NumberFormField
            placeholder="Quantity"
            control={control}
            name="quantity"
            error={errors.quantity}
            hint={errors.quantity?.message}
            fieldClass="mb-0"
          />
          <div className="d-flex justify-content-end mt-1">
            {inventoryId && (
              <>
                <MuteText>New Inventory</MuteText>
                <span className="ml-1">{newInventory}</span>
              </>
            )}
          </div>
        </div>
        <ControlledTextAreaFormField
          control={control}
          placeholder="Additional Note"
          label="Note"
          name="note"
          error={errors.note}
          hint={errors.note?.message}
          rows="3"
        />
        <div className="d-flex justify-content-end mt-4">
          <HoverBorderButton type="button" disabled={loading} onClick={onClose}>
            Cancel
          </HoverBorderButton>
          <StyledButton
            disabled={hasError}
            loading={loading}
            type="submit"
            className="ml-2"
          >
            Update
          </StyledButton>
        </div>
      </Form>
    </SkupremeModal>
  );
}

const WarningContainer = styled.div`
  background: #fef5e7;
  padding: 10px;
  border-radius: 5px;
  display: flex;
  column-gap: 10px;
  .icon {
    margin-bottom: -3px;
    min-width: 16px;
  }

  &.danger {
    background: rgba(255, 0, 0, 0.11);
    color: ${({ theme }) => theme.colors.danger};
  }

  margin-top: 10px;
`;

const InfoContainer = styled.div`
  background: ${({ theme }) => theme.colors.box};
  padding: 10px;
  border-radius: 5px;
  display: flex;
  column-gap: 10px;
  .icon {
    margin-bottom: -3px;
    min-width: 16px;
  }

  margin-top: 10px;
`;

function TransferMessage({
  destinationHasPallet,
  originTransferPallet,
  isEmpty,
  isOriginBin,
}) {
  if (isOriginBin && originTransferPallet) {
    if (!isEmpty) {
      return (
        <WarningContainer className="danger">
          <WarningCircle className="icon" color={theme.colors.red} size={16} />
          <span>To move entire container, the destination location must be empty</span>
        </WarningContainer>
      );
    }
  }

  if (originTransferPallet) {
    if (destinationHasPallet) {
      return (
        <WarningContainer>
          <Warning className="icon" color={theme.colors.yellow} size={16} />
          <span>
            The destination location already contains a pallet. By moving the pallet from
            the Origin Location, the pallet will be emptied into the Destination Location
            and freed up after the transfer
          </span>
        </WarningContainer>
      );
    }
    return (
      <InfoContainer>
        <Info className="icon" size={16} />
        <span>The Pallet along with items will be placed at destination location.</span>
      </InfoContainer>
    );
  }

  if (isEmpty) {
    return (
      <WarningContainer>
        <Warning className="icon" color={theme.colors.yellow} size={16} />
        <span>
          The destination location is empty. A new bin will be created for the transferred
          inventory
        </span>
      </WarningContainer>
    );
  }

  if (destinationHasPallet) {
    return (
      <InfoContainer>
        <Info className="icon" size={16} />
        <span>The items will be moved to the destination Pallet.</span>
      </InfoContainer>
    );
  }

  return (
    <InfoContainer>
      <Info className="icon" size={16} />
      <span>The items will be moved to the destination Bin.</span>
    </InfoContainer>
  );
}
